import type { CoachingSessionNote } from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import { cacheKeyFor, type Store } from '@warp-drive/core';
import {
  createRecord,
  query,
  updateRecord,
} from '@warp-drive/utilities/json-api';

import { jsonApiOptions, page } from '../../json-api.ts';

/**
 * An author keeps a single note per session, so only one record is expected.
 *
 * A small page is still requested rather than one, so that a duplicate created by a
 * race (two tabs saving a first note at once) is visible to the caller instead of
 * being silently hidden behind the page boundary.
 */
const MAX_NOTES_PER_SESSION = 5;

/**
 * The authenticated user's note for a session, newest first.
 *
 * ll-api scopes `coachingSessionNotes` to the caller's own notes via the standard
 * `user`-ownership handlers, so this never returns the other party's note even
 * though the filter here is only by session.
 */
export function querySessionNotesBySession(coachingSessionId: string) {
  return query<CoachingSessionNote>(
    DataTypeCoaching.CoachingSessionNote,
    {
      'filter[coachingSession.id]': coachingSessionId,
      ...page({ offset: 0, limit: MAX_NOTES_PER_SESSION }),
      'sort[]': '-createdAt',
    },
    jsonApiOptions
  );
}

export function createSessionNote(
  coachingSessionNote: CoachingSessionNote,
  store: Store
) {
  const init = createRecord(coachingSessionNote, {
    ...jsonApiOptions,
  });

  init.body = JSON.stringify({
    data: store.cache.peek(cacheKeyFor(coachingSessionNote)),
  });

  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}

export function updateSessionNote(
  coachingSessionNote: CoachingSessionNote,
  store: Store
) {
  const init = updateRecord(coachingSessionNote, {
    ...jsonApiOptions,
    patch: true,
  });

  init.body = JSON.stringify({
    data: store.cache.peek(cacheKeyFor(coachingSessionNote)),
  });

  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}
